'use client';

import { extractNumericValue } from '@/components/PrizeModal/hooks/usePrizeAnimation';

import { Fireworks, PrizeActions, PrizeInfo, TrophySection } from './components';
import { useFireworks, usePrizeAnimation } from './hooks';

export type PrizeQueuePosition = {
  current: number;
  total: number;
};

type PrizeModalProps = {
  prizeLabel: string;
  queuePosition?: PrizeQueuePosition;
  isBigGain?: boolean;
  showFireworks?: boolean;
  capitalizer?: string | null;
  onClose: () => void;
};

export const PrizeModal = ({
  prizeLabel,
  queuePosition,
  isBigGain = false,
  showFireworks: enableFireworks = true,
  capitalizer,
  onClose,
}: PrizeModalProps) => {
  const { animatedValue } = usePrizeAnimation({ prizeLabel });
  const { showFireworks } = useFireworks({
    isBigGain,
    enableFireworks,
  });
  const canRedraw = extractNumericValue(prizeLabel) < 2000;
  const hasQueue = Boolean(queuePosition && queuePosition.total > 1);
  const isLastPrize = !hasQueue || queuePosition!.current === queuePosition!.total;

  return (
    <div
      className="w-full relative rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md p-6 md:p-8 overflow-visible"
      style={{ boxShadow: '0 8px 30px rgba(2,6,23,0.6)' }}
      onClick={(e) => e.stopPropagation()}
    >
      {hasQueue && (
        <div
          data-cy="prize-queue-counter"
          className="absolute right-4 top-4 z-30 rounded-full border border-white/20 bg-white/15 px-2.5 py-1 text-xs font-semibold text-white/95"
        >
          {queuePosition!.current}/{queuePosition!.total}
        </div>
      )}
      <TrophySection />
      {isBigGain && enableFireworks && <Fireworks showFireworks={showFireworks} />}
      <PrizeInfo
        animatedValue={animatedValue}
        capitalizer={capitalizer}
        canRedraw={canRedraw}
      />
      <PrizeActions onClose={onClose} canRedraw={canRedraw} isLastPrize={isLastPrize} />
    </div>
  );
};
